import React, {
  type ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Flex } from '@banhala/ui-kit';
import { WebView } from 'shared/src/component/WebWebView';

// 상세설명 HTML 안의 iframe(유튜브 등)이 자체 스크롤/터치를 먹지 않게 막는다.
// 부모가 scrollEnabled={false} 여도 iframe 은 별도 browsing context 라 자체 스크롤이 된다.
// 콘텐츠는 보이되 내부 상호작용만 차단한다.
//
// ⚠️ 반드시 컨테이너 클래스로 스코프해야 한다. `<style>` 은 dangerouslySetInnerHTML 로
// **부모 문서에** 삽입되고 CSS 는 style 태그 위치와 무관하게 문서 전역에 적용되므로,
// 선택자를 `iframe` 으로 두면 앱의 모든 웹뷰 iframe(Cart/WebViewScreen 등)이 `!important`
// 로 클릭 불가가 된다. GoodsDetail 이 마운트된 동안 계속 남아 다른 화면까지 죽인다.
const CONTENT_SCOPE_CLASS = 'goods-content-html';

const IFRAME_STYLE = `
  <style>
    .${CONTENT_SCOPE_CLASS} iframe {
      pointer-events: none !important;
    }
  </style>
`;

const injectIframeStyle = (html: string): string => IFRAME_STYLE + html;

const HtmlContent = React.memo(({ html }: { html: string }) => (
  <div
    className={CONTENT_SCOPE_CLASS}
    dangerouslySetInnerHTML={{ __html: injectIframeStyle(html) }}
  />
));

const heightInjectJavascript = `(function () {
  if (window.__RN_HEIGHT_OBSERVER__) return;
  window.__RN_HEIGHT_OBSERVER__ = true;

  let lastHeight = 0;

  function postHeight() {
    const height = Math.ceil(
      document.documentElement.scrollHeight ||
      document.body.scrollHeight
    );

    if (height === lastHeight) return;
    lastHeight = height;

    // targetOrigin 을 생략하면 기본값이 '/'(동일 오리진)이라 cross-origin 문서에서
    // 조용히 버려진다. 높이 값은 민감 정보가 아니므로 '*' 로 보낸다.
    window.parent.postMessage(
      JSON.stringify({
        type: 'HEIGHT_CHANGE',
        height,
      }),
      '*'
    );
  }

  const ro = new ResizeObserver(postHeight);
  ro.observe(document.body);

  window.addEventListener('load', postHeight);
  postHeight();
})();`;

export const ContentWebView: React.FC<{
  source: { html?: string; uri?: string };
  onChangeHeight?: (height: number) => void;
  Footer?: ReactElement;
}> = props => {
  const [webviewHeight, setWebviewHeight] = useState<number>(200);

  // 개발 환경에서 CORS 우회를 위해 이미지 URL 변환
  const transformedHtml = useMemo(() => {
    // if (props.source.html) {
    //   return transformImageUrls(props.source.html);
    // }
    return props.source.html;
  }, [props.source.html]);

  const { onChangeHeight } = props;

  // iframe 안에서 주입 스크립트가 보내는 HEIGHT_CHANGE 를 받아 실제 높이를 반영한다.
  // WebWebView 가 window message 를 네이티브 webview 시그니처({ nativeEvent: { data } })로
  // 정규화해 넘겨주므로 그 형태로 받는다.
  const onWebViewMessage = useCallback(
    ({ nativeEvent }: { nativeEvent: { data?: string } }) => {
      const raw = nativeEvent?.data;
      if (raw == null) return;

      const parsed = ((): unknown => {
        try {
          return JSON.parse(raw);
        } catch {
          return null; // 다른 위젯이 보낸 비 JSON 메시지
        }
      })();

      if (
        parsed == null ||
        typeof parsed !== 'object' ||
        (parsed as { type?: unknown }).type !== 'HEIGHT_CHANGE'
      ) {
        return;
      }

      const height = Number((parsed as { height?: unknown }).height);
      if (!Number.isFinite(height) || height <= 0) return;

      setWebviewHeight(prev => (prev === height ? prev : height));
      onChangeHeight?.(height);
    },
    [onChangeHeight],
  );

  if (transformedHtml && transformedHtml.length > 0) {
    return (
      <Flex
        onLayout={({ nativeEvent }) => {
          const { height } = nativeEvent.layout;
          if (webviewHeight !== height) {
            props.onChangeHeight?.(height);
            setWebviewHeight(height);
          }
        }}
      >
        <HtmlContent html={transformedHtml} />

        {props.Footer}
      </Flex>
    );
  }

  if (props.source.uri) {
    return (
      <WebView
        // ref={ref}
        source={{ uri: props.source.uri, method: 'GET' }}
        // originWhitelist={['*']}
        onMessage={onWebViewMessage}
        scrollEnabled={false}
        // javaScriptEnabled={true}
        // cacheEnabled={true}
        // onLoadEnd={() => {
        // ref.current?.injectJavaScript(heightInjectJavascript);
        //}}
        injectedJavaScript={heightInjectJavascript}
        // style={{ height: 800 }}
        style={{ height: webviewHeight }}
      />
    );
  }
  return null;
};
